import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { assessmentRbacChecker } from "../../../../../utils/rbac";
import { getDressingChangeFrequency, updateDressingChangeFrequency } from "../../../../../app/patientSlice";
import { useReadOnly } from "../../../../../hooks/useReadOnly";

export const useDressingChangeFrequency = ({ interventionId }) => {
  const dispatch = useDispatch();
  const { patientId, assessmentId } = useParams();
  const { handleSubmit } = useForm();
  const isReadOnly = useReadOnly();

  const dressChange = useSelector((state) => state.patient.dressChangefrequency);
  const patientData = useSelector((state) => state.patient.detailViewData);
  const userDetails = useSelector((state) => state.user);

  const hideButtons = assessmentRbacChecker(userDetails?.role, patientData?.isOwnCustomer);

  const [editable, setEditable] = useState(false);
  const [formChangesConfirm, setFormChangesConfirm] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [clickCancel, setClickCancel] = useState(false);
  const [newfrequence, setNewFrequence] = useState(dressChange);
  const [numValidator, setNumValidator] = useState(null);

  const confirmationClose = useCallback(() => {
    setConfirmationModal(false);
    setClickCancel(false);
  }, []);

  const yesConfirmationModalClick = useCallback(() => {
    setConfirmationModal(false);
    setEditable(false);
    setFormChangesConfirm(false);
    setClickCancel(false);
  }, []);

  const handleEdit = useCallback(() => {
    setFormChangesConfirm(false);
    setClickCancel(false);
    setEditable(true);
  }, []);

  const handleCancel = useCallback(() => {
    toast.dismiss("success1");
    setClickCancel(true);
  }, []);

  const doSubmit = useCallback(() => {
    setEditable(false);
    dispatch(
      updateDressingChangeFrequency({
        patientId,
        assessmentId,
        interventionId,
        updateData: newfrequence,
      })
    );
  }, [dispatch, patientId, assessmentId, interventionId, newfrequence]);

  const getFrequency = useCallback(
    (e, ind, type) => {
      if (isNaN(e.target.value)) {
        setNumValidator(ind);
        e.target.value = null;
        setTimeout(() => setNumValidator(null), 2000);
      } else {
        setNumValidator(null);
      }

      const data = { ...newfrequence.data[type] };
      const catType = [...data.productCategory];
      const cat = { ...catType[ind] };
      cat.frequence = e.target.value;
      if (cat && cat.frequenceType === null) {
        cat.frequenceType = "Days";
      }
      catType.splice(ind, 1, cat);
      data.productCategory = catType;
      const update = { ...newfrequence.data };
      update[type] = data;
      setNewFrequence({ data: update });
    },
    [newfrequence]
  );

  const getFrequencyType = useCallback(
    (e, ind, type) => {
      const data = { ...newfrequence.data[type] };
      const catType = [...data.productCategory];
      const cat = { ...catType[ind] };
      cat.frequenceType = e.target.value;
      catType.splice(ind, 1, cat);
      data.productCategory = catType;
      const update = { ...newfrequence.data };
      update[type] = data;
      setNewFrequence({ data: update });
    },
    [newfrequence]
  );

  useEffect(() => {
    if (!editable) return;
    if (formChangesConfirm && clickCancel) {
      setConfirmationModal(true);
    } else if (clickCancel && !formChangesConfirm) {
      setClickCancel(false);
      setConfirmationModal(false);
      setEditable(false);
    }
  }, [formChangesConfirm, clickCancel, editable]);

  useEffect(() => {
    toast.dismiss("success1");
    dispatch(getDressingChangeFrequency({ patientId, assessmentId, interventionId }));
  }, [assessmentId, dispatch, interventionId, patientId]);

  useEffect(() => {
    setNewFrequence(dressChange);
  }, [dressChange]);

  return {
    editable,
    confirmationModal,
    numValidator,
    newfrequence,
    setFormChangesConfirm,
    dressChange,
    hideButtons,
    isReadOnly,
    handleSubmit,
    handleEdit,
    handleCancel,
    doSubmit,
    getFrequency,
    getFrequencyType,
    confirmationClose,
    yesConfirmationModalClick,
  };
};
